var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import * as PropTypes from 'prop-types';
import SearchBar from './../common/SearchBar';
import ListContainer from './../common/ListContainer';
import List from './../common/List';
import DropDownBase from '../common/DropDownBase';
import { itemIndexStartsWith, getItemValue, areSame, getFocusedItem } from '../common/utils';
import { guid, Keys, classNames } from '@progress/kendo-react-common';
import { FloatingLabel } from '@progress/kendo-react-labels';
import { validatePackage } from '@progress/kendo-licensing';
import { packageMetadata } from '../package-metadata';
var VALIDATION_MESSAGE = 'Please enter a valid value!';
/**
 * Represents the [KendoReact AutoComplete component]({% slug overview_autocomplete %}).
 *
 * @example
 * ```jsx
 * class App extends React.Component {
 *    autocomplete = null;
 *    render() {
 *       return (
 *          <div>
 *              <AutoComplete
 *                  data={[ "Albania", "Andorra", "Austria", "Belarus" ]}
 *                  ref={component => this.autocomplete = component}
 *              />
 *              <button onClick={() => alert(this.autocomplete.value)}>alert value</button>
 *          </div>
 *       );
 *    }
 * }
 * ReactDOM.render(<App />, document.querySelector('my-app'));
 * ```
 */
var AutoComplete = /** @class */ (function (_super) {
    __extends(AutoComplete, _super);
    function AutoComplete(props) {
        var _this = _super.call(this, props) || this;
        /**
         * @hidden
         */
        _this.state = {};
        _this.base = new DropDownBase(_this);
        _this._inputId = guid();
        _this._suggested = '';
        /**
         * @hidden
         */
        _this.focus = function () {
            if (_this._input) {
                _this._input.focus();
            }
        };
        /**
         * @hidden
         */
        _this.handleItemSelect = function (index, state) {
            var _a = _this.props.data, data = _a === void 0 ? [] : _a;
            var newText = getItemValue(data[index], _this.props.textField);
            _this.triggerOnChange(newText, state);
        };
        /**
         * @hidden
         */
        _this.itemFocus = function (index, state) {
            var _a = _this.props, _b = _a.data, data = _b === void 0 ? [] : _b, textField = _a.textField;
            var focusedItem = data[index];
            if (!areSame(_this.state.focusedItem, focusedItem, textField)) {
                state.data.focusedItem = focusedItem;
            }
        };
        /**
         * @hidden
         */
        _this.togglePopup = function (state) {
            _this.base.togglePopup(state);
        };
        _this.setValidity = function () {
            if (_this._input && _this._input.setCustomValidity) {
                _this._input.setCustomValidity(_this.validity.valid
                    ? ''
                    : _this.props.validationMessage || VALIDATION_MESSAGE);
            }
        };
        _this.handleItemClick = function (index, event) {
            _this.base.handleItemClick(index, event);
            _this._valueDuringOnChange = undefined;
        };
        _this.onChangeHandler = function (event) {
            var base = _this.base;
            var state = base.initState();
            var input = event.currentTarget;
            var value = input.value;
            var selectionAtEnd = input.selectionEnd === value.length;
            state.syntheticEvent = event;
            var prevSuggestion = _this._suggested;
            var prevValue = _this.value;
            var prevUserInput = prevValue &&
                prevValue.substring(0, prevValue.length - prevSuggestion.length);
            var deletedSuggestion = prevUserInput && prevUserInput === value;
            var deleting = prevUserInput && prevUserInput.length > value.length;
            var suggest = _this.props.suggest;
            var opened = _this.props.opened !== undefined ?
                _this.props.opened : _this.state.opened;
            if (suggest !== undefined && suggest !== false) {
                if (deletedSuggestion || deleting || !selectionAtEnd) {
                    _this._suggested = '';
                }
                else {
                    _this.suggestValue(value);
                }
                var newValue = value + _this._suggested;
                var suggestion = { userInput: value, value: _this._suggested };
                _this.triggerOnChange(newValue, state, { suggestion: suggestion });
            }
            else {
                _this._suggested = '';
                _this.triggerOnChange(value, state);
            }
            if ((!opened && value) || (opened && !value)) {
                _this.togglePopup(state);
            }
            state.data.focusedItem = undefined;
            _this.applyState(state);
        };
        _this.clearButtonClick = function (event) {
            var base = _this.base;
            var state = base.initState();
            var opened = _this.props.opened !== undefined ? _this.props.opened : _this.state.opened;
            state.syntheticEvent = event;
            var newValue = '';
            _this._suggested = '';
            _this.triggerOnChange(newValue, state);
            if (_this.state.focusedItem !== undefined) {
                state.data.focusedItem = undefined;
            }
            if (opened) {
                _this.togglePopup(state);
            }
            _this.applyState(state);
        };
        _this.onInputKeyDown = function (event) {
            var keyCode = event.keyCode;
            var opened = _this.props.opened !== undefined ? _this.props.opened : _this.state.opened;
            var state = _this.base.initState();
            state.syntheticEvent = event;
            var preventDefault = function () {
                if (opened) {
                    event.preventDefault();
                }
            };
            if (keyCode === Keys.enter || keyCode === Keys.esc) {
                preventDefault();
                _this.applyInputValue(event.currentTarget.value, state, event.keyCode);
            }
            else if (keyCode === Keys.up || keyCode === Keys.down) {
                preventDefault();
                _this.onNavigate(state, keyCode);
            }
        };
        _this.handleBlur = function (event) {
            if (_this.state.focused) {
                var state = _this.base.initState();
                state.data.focused = false;
                state.events.push({ type: 'onBlur' });
                state.syntheticEvent = event;
                _this.applyInputValue(event.currentTarget.value, state);
            }
        };
        validatePackage(packageMetadata);
        return _this;
    }
    Object.defineProperty(AutoComplete.prototype, "element", {
        /**
         * @hidden
         */
        get: function () {
            return this._element;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutoComplete.prototype, "value", {
        /**
         * The value of the AutoComplete.
         */
        get: function () {
            if (this._valueDuringOnChange !== undefined) {
                return this._valueDuringOnChange;
            }
            else if (this.props.value !== undefined) {
                return this.props.value;
            }
            else if (this.state.value !== undefined) {
                return this.state.value;
            }
            else if (this.props.defaultValue !== undefined) {
                return this.props.defaultValue;
            }
            return '';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutoComplete.prototype, "name", {
        /**
         * Gets the `name` property of the AutoComplete.
         */
        get: function () {
            return this.props.name;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutoComplete.prototype, "validity", {
        /**
         * Represents the validity state into which the AutoComplete is set.
         */
        get: function () {
            // The AutoComplete component is in a invalid state
            // only if it's required and the value is null!
            var customError = this.props.validationMessage !== undefined;
            var isValid = (!this.required || this.value !== '');
            var valid = this.props.valid !== undefined ? this.props.valid : isValid;
            return {
                customError: customError,
                valid: valid,
                valueMissing: this.value === null
            };
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutoComplete.prototype, "validityStyles", {
        /**
         * @hidden
         */
        get: function () {
            return this.props.validityStyles !== undefined
                ? this.props.validityStyles
                : AutoComplete.defaultProps.validityStyles;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutoComplete.prototype, "required", {
        /**
         * @hidden
         */
        get: function () {
            return this.props.required !== undefined
                ? this.props.required
                : AutoComplete.defaultProps.required;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    AutoComplete.prototype.componentDidUpdate = function (prevProps, prevState) {
        var _a = this.props.data, data = _a === void 0 ? [] : _a;
        var _b = prevProps.data, prevData = _b === void 0 ? [] : _b;
        var focusedIndex = this.focusedIndex();
        var focusedItem = data[focusedIndex];
        var dataChanged = prevData !== data;
        var focusedItemChanged = focusedItem !== undefined && prevState.focusedItem !== focusedItem;
        var opened = this.props.opened !== undefined ? this.props.opened : this.state.opened;
        var prevOpened = prevProps.opened !== undefined ? prevProps.opened : prevState.opened;
        var opening = !prevOpened && opened;
        if ((opened && (focusedItemChanged || dataChanged)) || opening) {
            this.base.scrollToItem(focusedIndex);
        }
        this.setValidity();
    };
    /**
     * @hidden
     */
    AutoComplete.prototype.componentDidMount = function () {
        this.base.didMount();
        this.setValidity();
    };
    /**
     * @hidden
     */
    AutoComplete.prototype.render = function () {
        var _this = this;
        var _a = this.props, dir = _a.dir, disabled = _a.disabled, label = _a.label, className = _a.className, style = _a.style, loading = _a.loading, suggest = _a.suggest;
        var isValid = !this.validityStyles || this.validity.valid;
        var focused = this.state.focused;
        var base = this.base;
        var value = this.value;
        var renderClearButton = !loading && !!value;
        var id = this.props.id || this._inputId;
        if (typeof suggest === 'string') {
            this._suggested = suggest;
        }
        var autoComplete = (React.createElement("span", { className: classNames('k-widget k-autocomplete k-header', className, {
                'k-state-disabled': disabled,
                'k-state-focused': focused && !disabled,
                'k-state-invalid': !isValid
            }), ref: function (element) {
                _this._element = element;
                base.wrapper = element;
            }, style: !label
                ? style
                : __assign({}, style, { width: undefined }), dir: dir },
            this.renderSearchBar(renderClearButton, value || '', id),
            loading && React.createElement("span", { className: "k-icon k-i-loading" }),
            this.renderListContainer()));
        return label
            ? (React.createElement(FloatingLabel, { label: label, editorId: id, editorValue: value, editorValid: isValid, editorDisabled: disabled, style: { width: style ? style.width : undefined }, children: autoComplete }))
            : autoComplete;
    };
    /**
     * @hidden
     */
    AutoComplete.prototype.onNavigate = function (state, keyCode) {
        var _this = this;
        var typedText = this.value;
        var _a = this.props, _b = _a.data, data = _b === void 0 ? [] : _b, textField = _a.textField, focusedItemIndex = _a.focusedItemIndex;
        var focusedIndex = this.state.focusedItem !== undefined ?
            data.findIndex(function (i) { return areSame(i, _this.state.focusedItem, textField); }) :
            (focusedItemIndex ? focusedItemIndex(data, typedText, textField) :
                data.indexOf(getFocusedItem(data, typedText, textField)));
        var newFocused = this.base.navigation.navigate({
            keyCode: keyCode,
            current: focusedIndex,
            max: data.length - 1,
            min: 0
        });
        if (newFocused !== undefined) {
            this.itemFocus(newFocused, state);
        }
        this.applyState(state);
    };
    /**
     * @hidden
     */
    AutoComplete.prototype.applyInputValue = function (value, state, eventKey) {
        var opened = this.props.opened !== undefined ? this.props.opened : this.state.opened;
        var _a = this.props, _b = _a.data, data = _b === void 0 ? [] : _b, textField = _a.textField;
        this._suggested = '';
        if (opened && eventKey === Keys.enter) {
            var newValue = getItemValue(data[this.focusedIndex(value)], textField);
            this.triggerOnChange(newValue, state);
        }
        if (opened) {
            this.togglePopup(state);
        }
        this.applyState(state);
    };
    AutoComplete.prototype.renderSearchBar = function (clearButton, value, id) {
        var _this = this;
        var base = this.base;
        var _a = this.props, placeholder = _a.placeholder, tabIndex = _a.tabIndex, disabled = _a.disabled, readonly = _a.readonly;
        var focused = this.state.focused;
        var opened = this.props.opened !== undefined ? this.props.opened : this.state.opened;
        return (React.createElement(SearchBar, { id: id, placeholder: placeholder, tabIndex: tabIndex || undefined, accessKey: this.props.accessKey, value: value, suggestedText: this._suggested, focused: focused, name: this.props.name, ref: function (searchbar) { return _this._input = searchbar && searchbar.input; }, onKeyDown: this.onInputKeyDown, onChange: this.onChangeHandler, onFocus: base.handleFocus, onBlur: this.handleBlur, disabled: disabled, readOnly: readonly, expanded: opened, owns: base.listBoxId, activedescendant: 'option-' + base.guid + '-' + this.focusedIndex(), role: "combobox", clearButton: clearButton, clearButtonClick: this.clearButtonClick, ariaLabelledBy: this.props.ariaLabelledBy, ariaDescribedBy: this.props.ariaDescribedBy }));
    };
    AutoComplete.prototype.renderListContainer = function () {
        var base = this.base;
        var _a = this.props, dir = _a.dir, header = _a.header, footer = _a.footer, _b = _a.data, data = _b === void 0 ? [] : _b;
        var popupSettings = base.getPopupSettings();
        var opened = this.props.opened !== undefined ? this.props.opened : this.state.opened;
        var popupWidth = popupSettings.width !== undefined ? popupSettings.width : base.popupWidth;
        return (React.createElement(ListContainer, { width: popupWidth, popupSettings: {
                animate: popupSettings.animate,
                anchor: this.element,
                show: opened,
                className: classNames('k-list-container k-reset', popupSettings.className),
                appendTo: popupSettings.appendTo
            }, dir: dir !== undefined ? dir : this.base.dirCalculated, itemsCount: [data.length] },
            header,
            this.renderList(),
            footer));
    };
    AutoComplete.prototype.renderList = function () {
        var base = this.base;
        var popupSettings = base.getPopupSettings();
        var _a = this.props, textField = _a.textField, _b = _a.data, data = _b === void 0 ? [] : _b, listNoDataRender = _a.listNoDataRender, itemRender = _a.itemRender;
        var value = this.value;
        var opened = this.props.opened !== undefined ? this.props.opened : this.state.opened;
        return (React.createElement(List, { id: base.listBoxId, show: opened, data: data.slice(), focusedIndex: this.focusedIndex(), value: value, textField: textField, valueField: textField, highlightSelected: false, optionsGuid: base.guid, listRef: function (list) { return base.list = list; }, wrapperStyle: { maxHeight: popupSettings.height }, wrapperCssClass: "k-list-scroller", onClick: this.handleItemClick, itemRender: itemRender, noDataRender: listNoDataRender, onMouseDown: function (e) { return e.preventDefault(); } }));
    };
    AutoComplete.prototype.triggerOnChange = function (newValue, state, eventArgs) {
        if (this.value === newValue && !eventArgs) {
            return;
        }
        state.data.value = newValue;
        this._valueDuringOnChange = newValue;
        state.events.push(__assign({ type: 'onChange' }, (eventArgs || {})));
    };
    AutoComplete.prototype.applyState = function (state) {
        this.base.applyState(state);
        this._valueDuringOnChange = undefined;
    };
    AutoComplete.prototype.suggestValue = function (value) {
        this._suggested = '';
        if (value) {
            var _a = this.props, _b = _a.data, data = _b === void 0 ? [] : _b, textField = _a.textField;
            var suggestedItem = data[itemIndexStartsWith(data, value, textField)];
            if (suggestedItem) {
                var suggestedText = getItemValue(suggestedItem, textField);
                if (value.toLowerCase() !== suggestedText.toLowerCase()) {
                    this._suggested = suggestedText.substring(value.length);
                }
            }
        }
    };
    AutoComplete.prototype.focusedIndex = function (value) {
        var _this = this;
        var _a = this.props, _b = _a.data, data = _b === void 0 ? [] : _b, textField = _a.textField, focusedItemIndex = _a.focusedItemIndex;
        var inputValue = value !== undefined ? value : this.value;
        return this.state.focusedItem !== undefined ?
            data.findIndex(function (i) { return areSame(i, _this.state.focusedItem, textField); }) :
            (focusedItemIndex ? focusedItemIndex(data, inputValue, textField) :
                Math.max(0, data.indexOf(getFocusedItem(data, inputValue, textField))));
    };
    /**
     * @hidden
     */
    AutoComplete.propTypes = __assign({}, DropDownBase.basicPropTypes, { suggest: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]), placeholder: PropTypes.string, value: PropTypes.string, defaultValue: PropTypes.string, validationMessage: PropTypes.string, required: PropTypes.bool, readonly: PropTypes.bool, id: PropTypes.string, ariaLabelledBy: PropTypes.string, ariaDescribedBy: PropTypes.string });
    /**
     * @hidden
     */
    AutoComplete.defaultProps = __assign({}, DropDownBase.defaultProps);
    return AutoComplete;
}(React.Component));
export default AutoComplete;
//# sourceMappingURL=AutoComplete.js.map